import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./PaymentCallBack.css"; // reuse the same CSS

const PaymentOutcome = () => {
  const navigate = useNavigate();
  const { currentUser, userSettings, loading } = useAuth();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // ✅ Safety: if user is not logged in, go login
  if (!loading && !currentUser) {
    navigate("/authentication", { replace: true });
    return null;
  }

  // ✅ Optional: if userSettings not loaded yet
  if (loading || !userSettings) {
    return (
      <main className="home">
        <section className="checkout payment-callback">
          <div className="checkout-header">
            <h2>
              Payment <span className="gradient-text">Outcome</span>
            </h2>
            <p className="checkout-subtitle">Loading your payment status...</p>
          </div>
          <div className="checkout-grid payment-grid">
            <div className="checkout-card featured payment-card">
              <p className="success">Please wait...</p>
            </div>
          </div>
        </section>
      </main>
    );
  }


  const handleProceed = async () => {
    navigate("/workspace", { replace: true });
  };

  const handleRetry = async () => {
    navigate("/workspace", { replace: true });
  };

  const handleSkipForNow = async () => {
    navigate("/workspace", { replace: true });
  };

  const showSuccess = userSettings?.upgradePlanStatus === "upgraded" || userSettings?.scheduledPlanChangeType === "downgrade";
  const showFailed = userSettings?.upgradePlanStatus === "failed";

  if (!showSuccess) {
    setError("Something went wrong. Please try again.")
  }

  if (!userSettings) {
    setBusy(true);
    setBusy(false);
  }
  
  return (
    <main className="home">
      <section className="checkout payment-callback">
        <div className="checkout-header">
          <h2>
            Payment <span className="gradient-text">Outcome</span>
          </h2>
          <p className="checkout-subtitle">
            This page shows the result of your payment. Choose what to do next.
          </p>
        </div>

        <div className="checkout-grid payment-grid">
          <div className="checkout-card featured payment-card">
            <h3>Payment Result</h3>

            {error && (
              <div className="payment-state">
                <p className="error">{error}</p>
              </div>
            )}

            {/* ✅ SUCCESS */}
            {showSuccess && (
              <div className="payment-state">
                <div className="payment-badge success-badge">Payment Successful</div>
                <p className="payment-title">Congratulations 🎉</p>
                <p className="payment-text">
                  Your payment has been confirmed. Click <b>Proceed</b> to activate your plan.
                </p>

                <div className="payment-actions">
                  <button className="primary-btn" type="button" onClick={handleProceed} disabled={busy}>
                    {busy ? "Please wait..." : "Proceed"}
                  </button>
                </div>
              </div>
            )}

            {/* ✅ FAILED */}
            {showFailed && (
              <div className="payment-state">
                <div className="payment-badge fail-badge">Payment Failed</div>
                <p className="payment-title">We couldn’t confirm your payment</p>
                <p className="payment-text">
                  Your payment did not complete successfully. You can retry, skip for now, or return to pricing.
                </p>

                <div className="payment-actions">
                  <button className="primary-btn" type="button" onClick={handleRetry} disabled={busy}>
                    {busy ? "Please wait..." : "Retry Payment"}
                  </button>
                  <button className="secondary-btn" type="button" onClick={handleSkipForNow} disabled={busy}>
                    Skip for now
                  </button>
                </div>
              </div>
            )}

            {/* ✅ If status is something else */}
            {!showSuccess && !showFailed && (
              <div className="payment-state">
                <p className="muted">
                  Your current status is: <b>{userSettings?.planStatus || "—"}</b>
                </p>
                <p className="payment-text">
                  If you were expecting a result, please return to pricing and try again.
                </p>
              </div>
            )}

            <div className="payment-meta">
              <div className="mini-row">
                <span>Plan</span>
                <span className="muted">{userSettings?.upgradePlanKey || "—"}</span>
              </div>
              <div className="mini-row">
                <span>Status</span>
                <span className="muted">{userSettings?.upgradePlanStatus || "—"}</span>
              </div>
              <div className="mini-row">
                <span>Reference</span>
                <span className="muted">{userSettings?.upgradeLastPaymentReference || "—"}</span>
              </div>
            </div>

            <p className="tiny payment-footnote">
              You won’t be redirected automatically. You are in control of what happens next.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PaymentOutcome;
